import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Clock, Gamepad2, Target, Calendar } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * OBS Fullscreen Card - 1920x1080 Browser Source
 * Designed for stream starting/BRB screens and dedicated challenge displays
 */
const OBSFullscreenCard = () => {
  const { challengeId } = useParams();
  const [searchParams] = useSearchParams();
  const [challenge, setChallenge] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [needsScroll, setNeedsScroll] = useState(false);
  const rulesContainerRef = useRef(null);
  const rulesListRef = useRef(null);

  const theme = searchParams.get('theme') || 'dark';

  // Theme configurations
  const themeConfig = {
    dark: { 
      accent: '#00ff00',
      accentRgb: '0, 255, 0',
      text: '#ffffff',
      secondary: '#888888',
      gradient: 'from-green-900/20 to-black',
    },
    neon: {
      accent: '#00ffff',
      accentRgb: '0, 255, 255',
      text: '#ffffff',
      secondary: '#a855f7',
      gradient: 'from-cyan-900/20 to-black',
    },
    red: {
      accent: '#ff4444',
      accentRgb: '255, 68, 68',
      text: '#ffffff',
      secondary: '#ff8888',
      gradient: 'from-red-900/20 to-black',
    },
    gold: {
      accent: '#ffd700',
      accentRgb: '255, 215, 0',
      text: '#ffffff',
      secondary: '#ffaa00',
      gradient: 'from-yellow-900/20 to-black',
    }
  };

  const currentTheme = themeConfig[theme] || themeConfig.dark;

  // Check if rules need scrolling
  const checkOverflow = useCallback(() => {
    if (rulesContainerRef.current && rulesListRef.current) {
      const containerHeight = rulesContainerRef.current.clientHeight;
      const listHeight = rulesListRef.current.scrollHeight;
      setNeedsScroll(listHeight > (containerHeight - 20));
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(checkOverflow, 100);
    window.addEventListener('resize', checkOverflow);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [challenge, checkOverflow]);

  // Fetch challenge data
  useEffect(() => {
    const fetchChallenge = async () => {
      try {
        const response = await axios.get(`${API}/api/challenges`);
        let found = null;
        
        if (challengeId && challengeId !== 'active') {
          found = response.data.find(c => c.id === challengeId);
        } else {
          found = response.data.find(c => c.status === 'active') || 
                  response.data.find(c => c.status === 'scheduled');
        }
        
        if (found) {
          setChallenge(found);
          setError(null);
        } else {
          setError('No challenge found');
        }
      } catch (err) {
        setError('Failed to load challenge');
      } finally {
        setLoading(false);
      }
    };

    fetchChallenge();
    const interval = setInterval(fetchChallenge, 30000);
    return () => clearInterval(interval);
  }, [challengeId]);

  // Timer logic
  useEffect(() => {
    if (!challenge) return;

    const isLive = challenge.status === 'active';
    const isScheduled = challenge.status === 'scheduled';
    const targetTime = isLive ? challenge.end_time : 
                       isScheduled ? challenge.start_time : null;
    
    if (!targetTime) {
      setTimeLeft(null);
      return;
    }

    const updateTimer = () => {
      const target = new Date(targetTime);
      const now = new Date();
      const diff = target - now;

      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: true });
        return;
      }

      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000),
        expired: false
      });
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [challenge]);

  if (loading) {
    return (
      <div className="w-[1920px] h-[1080px] flex items-center justify-center" style={{ background: 'transparent' }}>
        <div className="animate-pulse text-3xl" style={{ color: currentTheme.accent }}>Loading Challenge...</div>
      </div>
    );
  }

  if (error || !challenge) {
    return (
      <div className="w-[1920px] h-[1080px] flex items-center justify-center" style={{ background: 'transparent' }}>
        <div className="text-3xl" style={{ color: currentTheme.secondary }}>{error || 'No challenge available'}</div>
      </div>
    );
  }

  const isLive = challenge.status === 'active';
  const isScheduled = challenge.status === 'scheduled';
  
  // Get rules based on visibility settings
  const getVisibleRules = () => {
    // If rules_config exists, use visibility settings
    if (challenge.rules_config && challenge.rules_config.length > 0) {
      if (isLive) {
        // Show all rules when live
        return challenge.rules_config.map(r => ({
          text: r.text,
          visibility: r.visibility
        }));
      } else if (isScheduled) {
        // For scheduled: show 'always' normally, 'blur_scheduled' blurred, hide 'live_only'
        return challenge.rules_config
          .filter(r => r.visibility === 'always' || r.visibility === 'blur_scheduled')
          .map(r => ({
            text: r.text,
            visibility: r.visibility,
            shouldBlur: r.visibility === 'blur_scheduled'
          }));
      }
    }
    // Fallback to legacy rules string
    const legacyRules = challenge.rules ? challenge.rules.split('\n').filter(r => r.trim()) : [];
    return legacyRules.map(text => ({ text, visibility: 'always', shouldBlur: false }));
  };
  
  const rules = getVisibleRules();
  const hasRulesToShow = rules.length > 0;
  const timerLabel = isLive ? 'TIME REMAINING' : isScheduled ? 'STARTS IN' : '';
  const timerColor = isLive ? currentTheme.accent : '#ffaa00';

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <div className="w-[1920px] h-[1080px] p-8" style={{ background: 'transparent' }}>
      <div 
        className="w-full h-full rounded-3xl flex overflow-hidden relative"
        style={{
          background: `linear-gradient(135deg, rgba(${currentTheme.accentRgb}, 0.15) 0%, rgba(0,0,0,0.95) 50%, rgba(0,0,0,0.98) 100%)`,
          border: `3px solid rgba(${currentTheme.accentRgb}, 0.6)`,
          boxShadow: `0 0 100px rgba(${currentTheme.accentRgb}, 0.3), inset 0 0 100px rgba(0,0,0,0.5)`,
        }}
      >
        {/* Decorative Corner Elements */}
        <div 
          className="absolute top-0 left-0 w-40 h-40"
          style={{
            background: `linear-gradient(135deg, rgba(${currentTheme.accentRgb}, 0.3) 0%, transparent 70%)`,
          }}
        />
        <div 
          className="absolute bottom-0 right-0 w-40 h-40"
          style={{
            background: `linear-gradient(-45deg, rgba(${currentTheme.accentRgb}, 0.3) 0%, transparent 70%)`,
          }}
        />

        {/* Left Section - Main Content (2/3 width) */}
        <div className="flex-1 flex flex-col p-12 relative z-10">
          
          {/* Top Section - Status & Title */}
          <div className="mb-8">
            {/* Status Badge */}
            <div className="flex items-center gap-4 mb-6">
              <span 
                className="inline-flex items-center gap-3 px-6 py-2 rounded-full text-lg font-black uppercase tracking-widest"
                style={{
                  background: isLive ? `rgba(${currentTheme.accentRgb}, 0.2)` : 'rgba(255, 170, 0, 0.2)',
                  color: isLive ? currentTheme.accent : '#ffaa00',
                  border: `3px solid ${isLive ? currentTheme.accent : '#ffaa00'}`,
                  boxShadow: `0 0 30px ${isLive ? `rgba(${currentTheme.accentRgb}, 0.5)` : 'rgba(255, 170, 0, 0.5)'}`
                }}
              >
                <span 
                  className={`w-3 h-3 rounded-full ${isLive ? 'animate-pulse' : ''}`}
                  style={{ background: isLive ? currentTheme.accent : '#ffaa00' }}
                />
                {isLive ? 'CHALLENGE LIVE' : isScheduled ? 'COMING SOON' : challenge.status?.toUpperCase()}
              </span>

              {/* Date Info */}
              {(challenge.start_time || challenge.end_time) && (
                <div className="flex items-center gap-2 text-lg" style={{ color: currentTheme.secondary }}>
                  <Calendar className="w-5 h-5" />
                  <span>{formatDate(isLive ? challenge.end_time : challenge.start_time)}</span>
                </div>
              )}
            </div>
            
            {/* Challenge Title */}
            <h1 
              className="text-7xl font-black tracking-tight mb-4 leading-tight"
              style={{ 
                color: currentTheme.text,
                textShadow: `0 0 60px rgba(${currentTheme.accentRgb}, 0.5), 0 4px 20px rgba(0,0,0,0.8)`
              }}
            >
              {challenge.title}
            </h1>
            
            {/* Description */}
            {challenge.description && (
              <p 
                className="text-2xl leading-relaxed max-w-4xl"
                style={{ color: 'rgba(255,255,255,0.7)' }}
              >
                {challenge.description}
              </p>
            )}
          </div>

          {/* Middle Section - Game & Prize */}
          <div className="flex items-center gap-8 mb-8">
            {/* Game Cover */}
            {challenge.game_cover_url && (
              <img 
                src={challenge.game_cover_url} 
                alt={challenge.game_name}
                className="w-48 h-64 object-cover rounded-2xl flex-shrink-0"
                style={{ 
                  boxShadow: `0 0 50px rgba(${currentTheme.accentRgb}, 0.5), 0 10px 40px rgba(0,0,0,0.8)`,
                  border: `3px solid rgba(${currentTheme.accentRgb}, 0.5)`
                }}
              />
            )}
            
            <div className="flex-1">
              {/* Game Name */}
              {challenge.game_name && (
                <div className="flex items-center gap-3 text-2xl mb-6" style={{ color: currentTheme.secondary }}>
                  <Gamepad2 className="w-8 h-8" />
                  <span className="font-semibold">{challenge.game_name}</span>
                </div>
              )}

              {/* Prize Box */}
              <div 
                className="p-6 rounded-2xl flex-1"
                style={{
                  background: `linear-gradient(135deg, rgba(${currentTheme.accentRgb}, 0.2) 0%, rgba(${currentTheme.accentRgb}, 0.05) 100%)`,
                  border: `2px solid rgba(${currentTheme.accentRgb}, 0.4)`,
                  boxShadow: `0 0 40px rgba(${currentTheme.accentRgb}, 0.2)`
                }}
              >
                {/* Header */}
                <div className="flex items-center gap-4 mb-4">
                  <Trophy className="w-10 h-10" style={{ color: currentTheme.accent }} />
                  <div className="text-lg uppercase tracking-widest font-medium" style={{ color: currentTheme.secondary }}>
                    Prize Pool {challenge.max_winners > 1 && <span style={{ color: currentTheme.accent }}>• {challenge.max_winners} Winners</span>}
                  </div>
                </div>

                {/* Prize Display - All in one row */}
                <div className="flex items-center gap-8">
                  {/* 1st Place */}
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">🥇</span>
                    <div>
                      <div 
                        className="text-5xl font-black"
                        style={{ 
                          color: currentTheme.accent,
                          textShadow: `0 0 30px rgba(${currentTheme.accentRgb}, 0.8)`
                        }}
                      >
                        {challenge.prize_1st_config?.prize_type === 'game_code' 
                          ? (challenge.prize_1st_config?.description || '🎮 Game Code')
                          : challenge.prize_1st_config?.prize_type === 'item' 
                            ? (challenge.prize_1st_config?.description || '🎁 Item')
                            : `$${challenge.prize_1st || challenge.prize_amount || 0}`}
                      </div>
                    </div>
                  </div>

                  {/* 2nd Place */}
                  {(challenge.max_winners >= 2 || challenge.prize_2nd || challenge.prize_2nd_config) && (
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">🥈</span>
                      <div 
                        className="text-3xl font-black"
                        style={{ color: '#c0c0c0' }}
                      >
                        {challenge.prize_2nd_config?.prize_type === 'game_code' 
                          ? (challenge.prize_2nd_config?.description || '🎮 Game Code')
                          : challenge.prize_2nd_config?.prize_type === 'item' 
                            ? (challenge.prize_2nd_config?.description || '🎁 Item')
                            : `$${challenge.prize_2nd || 0}`}
                      </div>
                    </div>
                  )}

                  {/* 3rd Place */}
                  {(challenge.max_winners >= 3 || challenge.prize_3rd || challenge.prize_3rd_config) && (
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">🥉</span>
                      <div 
                        className="text-3xl font-black"
                        style={{ color: '#cd7f32' }}
                      >
                        {challenge.prize_3rd_config?.prize_type === 'game_code' 
                          ? (challenge.prize_3rd_config?.description || '🎮 Game Code')
                          : challenge.prize_3rd_config?.prize_type === 'item' 
                            ? (challenge.prize_3rd_config?.description || '🎁 Item')
                            : `$${challenge.prize_3rd || 0}`}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Section - Timer */}
          {timeLeft && !timeLeft.expired && (
            <div 
              className="mt-auto p-8 rounded-2xl"
              style={{
                background: isLive ? `rgba(${currentTheme.accentRgb}, 0.1)` : 'rgba(255, 170, 0, 0.1)',
                border: `2px solid ${isLive ? `rgba(${currentTheme.accentRgb}, 0.3)` : 'rgba(255, 170, 0, 0.4)'}`,
              }}
            >
              <div 
                className="flex items-center gap-3 text-xl mb-6 uppercase tracking-widest font-bold" 
                style={{ color: isLive ? currentTheme.secondary : '#ffaa00' }}
              >
                <Clock className="w-6 h-6" />
                {timerLabel}
              </div>
              
              <div className="flex items-baseline gap-4">
                {timeLeft.days > 0 && (
                  <>
                    <div className="text-center">
                      <span 
                        className="text-9xl font-black font-mono"
                        style={{ 
                          color: currentTheme.text,
                          textShadow: `0 0 30px rgba(${currentTheme.accentRgb}, 0.4)`
                        }}
                      >
                        {String(timeLeft.days).padStart(2, '0')}
                      </span>
                      <span className="text-2xl ml-2 uppercase font-medium" style={{ color: currentTheme.secondary }}>DAYS</span>
                    </div>
                    <span className="text-7xl font-bold" style={{ color: timerColor }}>:</span>
                  </>
                )}
                
                <div className="text-center">
                  <span 
                    className="text-9xl font-black font-mono"
                    style={{ 
                      color: currentTheme.text,
                      textShadow: `0 0 30px rgba(${currentTheme.accentRgb}, 0.4)`
                    }}
                  >
                    {String(timeLeft.hours).padStart(2, '0')}
                  </span>
                  <span className="text-2xl ml-2 uppercase font-medium" style={{ color: currentTheme.secondary }}>HRS</span>
                </div>
                
                <span className="text-7xl font-bold" style={{ color: timerColor }}>:</span>
                
                <div className="text-center">
                  <span 
                    className="text-9xl font-black font-mono"
                    style={{ 
                      color: currentTheme.text,
                      textShadow: `0 0 30px rgba(${currentTheme.accentRgb}, 0.4)`
                    }}
                  >
                    {String(timeLeft.minutes).padStart(2, '0')}
                  </span>
                  <span className="text-2xl ml-2 uppercase font-medium" style={{ color: currentTheme.secondary }}>MIN</span>
                </div>
                
                <span className="text-7xl font-bold" style={{ color: timerColor }}>:</span>
                
                <div className="text-center">
                  <span 
                    className="text-9xl font-black font-mono animate-pulse"
                    style={{ 
                      color: timerColor,
                      textShadow: `0 0 40px ${timerColor}`
                    }}
                  >
                    {String(timeLeft.seconds).padStart(2, '0')}
                  </span>
                  <span className="text-2xl ml-2 uppercase font-medium" style={{ color: currentTheme.secondary }}>SEC</span>
                </div>
              </div>
            </div>
          )}

          {/* Bounties Section - Between timer and branding */}
          {challenge.bounties && challenge.bounties.length > 0 && (
            <div 
              className="mt-6 p-6 rounded-2xl"
              style={{
                background: 'linear-gradient(135deg, rgba(255,170,0,0.15), rgba(255,100,0,0.08))',
                border: '2px solid rgba(255, 170, 0, 0.4)',
              }}
            >
              <div 
                className="flex items-center gap-3 text-xl mb-6 uppercase tracking-widest font-bold" 
                style={{ color: '#ffaa00' }}
              >
                <Trophy className="w-6 h-6" />
                Bonus Bounties
              </div>
              
              <div className="space-y-4">
                {challenge.bounties.map((bounty, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <span className="text-3xl">⭐</span>
                    <div className="flex-1">
                      <p className="text-2xl font-bold" style={{ color: '#ffcc00' }}>
                        {bounty.prize}
                      </p>
                      <p className="text-lg mt-1" style={{ color: 'rgba(255,255,255,0.7)' }}>
                        └─ {bounty.rule}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Branding */}
          <div className="mt-6 text-center text-xl font-medium" style={{ color: currentTheme.secondary }}>
            cesarmchallenges.com
          </div>
        </div>

        {/* Right Section - Rules (1/3 width) - Show for LIVE and SCHEDULED with visibility settings */}
        {(isLive || isScheduled) && hasRulesToShow && (
          <div 
            className="w-[550px] flex-shrink-0 flex flex-col relative"
            style={{
              background: 'linear-gradient(180deg, rgba(255, 68, 68, 0.12) 0%, rgba(255, 68, 68, 0.05) 100%)',
              borderLeft: `2px solid rgba(255, 68, 68, 0.4)`
            }}
          >
            {/* Rules Header */}
            <div 
              className="p-6 text-2xl uppercase tracking-widest font-black flex-shrink-0 flex items-center gap-3"
              style={{ 
                color: '#ff6666',
                borderBottom: '2px solid rgba(255, 68, 68, 0.3)',
                background: 'rgba(255, 68, 68, 0.15)'
              }}
            >
              <Target className="w-7 h-7" />
              Challenge Rules
            </div>
            
            {/* Rules List */}
            <div 
              className="flex-1 overflow-hidden relative"
              ref={rulesContainerRef}
            >
              <div 
                className="p-6"
                style={{
                  animation: needsScroll ? 'scrollRulesFull 50s linear infinite' : 'none'
                }}
              >
                <ul className="space-y-5" ref={rulesListRef}>
                  {rules.map((rule, i) => (
                    <li 
                      key={i} 
                      className="flex items-start gap-4 text-xl"
                      style={{ 
                        color: 'rgba(255,255,255,0.9)',
                        filter: rule.shouldBlur ? 'blur(8px)' : 'none',
                        userSelect: rule.shouldBlur ? 'none' : 'auto'
                      }}
                    >
                      <span 
                        className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold"
                        style={{ 
                          background: `rgba(${currentTheme.accentRgb}, 0.3)`,
                          color: currentTheme.accent,
                          border: `2px solid rgba(${currentTheme.accentRgb}, 0.5)`
                        }}
                      >
                        {i + 1}
                      </span>
                      <span 
                        className="leading-relaxed flex-1 pt-1"
                        style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}
                      >
                        {rule.text}
                      </span>
                    </li>
                  ))}
                </ul>
                
                {/* Duplicate for seamless scroll - for both LIVE and SCHEDULED when overflow */}
                {needsScroll && (
                  <ul className="space-y-5 mt-8">
                    {rules.map((rule, i) => (
                      <li 
                        key={`dup-${i}`} 
                        className="flex items-start gap-4 text-xl"
                        style={{ 
                          color: 'rgba(255,255,255,0.9)',
                          filter: rule.shouldBlur ? 'blur(8px)' : 'none',
                          userSelect: rule.shouldBlur ? 'none' : 'auto'
                        }}
                      >
                        <span 
                          className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold"
                          style={{ 
                            background: `rgba(${currentTheme.accentRgb}, 0.3)`,
                            color: currentTheme.accent,
                            border: `2px solid rgba(${currentTheme.accentRgb}, 0.5)`
                          }}
                        >
                          {i + 1}
                        </span>
                        <span 
                          className="leading-relaxed flex-1 pt-1"
                          style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}
                        >
                          {rule.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              
              {/* Show "Some rules hidden" hint for scheduled with hidden rules */}
              {isScheduled && challenge.rules_config?.some(r => r.visibility === 'live_only') && (
                <div className="absolute bottom-4 left-0 right-0 flex justify-center pointer-events-none">
                  <div 
                    className="px-4 py-2 rounded-lg text-sm font-medium"
                    style={{
                      background: 'rgba(0, 0, 0, 0.8)',
                      color: '#ffaa00',
                      border: '1px solid rgba(255, 170, 0, 0.4)'
                    }}
                  >
                    More rules revealed when live
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      <style>{`
        @keyframes scrollRulesFull {
          0% { transform: translateY(0); }
          100% { transform: translateY(-50%); }
        }
      `}</style>
    </div>
  );
};

export default OBSFullscreenCard;
